import { createFileRoute } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useMemo, useState } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Video,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AutoFillDialog } from "@/components/library/AutoFillDialog";
import { MediaPicker } from "@/components/library/MediaPicker";


type ItemType = "technique" | "drill" | "recommendation" | "resource";

type Item = {
  id: string;
  title: string;
  description: string | null;
  body_text: string | null;
  video_url: string | null;
  tags: string[] | null;
  item_type: ItemType;
  image_url: string | null;
  image_path: string | null;
  parent_id: string | null;
  position: number;
  updated_at: string | null;
};

const TYPE_LABEL: Record<ItemType | "all", string> = {
  all: "All",
  technique: "Techniques",
  drill: "Drills",
  recommendation: "Recommendations",
  resource: "Resources",
};

const TYPE_SINGULAR: Record<ItemType, string> = {
  technique: "technique",
  drill: "drill",
  recommendation: "recommendation",
  resource: "resource",
};

const TYPE_META: Record<ItemType, { label: string; cls: string; icon: typeof Zap | null }> = {
  technique: {
    label: "TECHNIQUE",
    cls: "bg-[#e2f6d5] text-[#054d28]",
    icon: Zap,
  },
  drill: {
    label: "DRILL",
    cls: "bg-[#cdffad] text-[#054d28]",
    icon: null,
  },
  recommendation: {
    label: "RECOMMENDATION",
    cls: "bg-[#fff3c2] text-[#b86700]",
    icon: null,
  },
  resource: {
    label: "RESOURCE",
    cls: "bg-[#e6f0f8] text-[#1d4ed8]",
    icon: null,
  },
};

function timeAgo(iso: string | null | undefined) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86400000);
  if (days < 1) {
    const hrs = Math.floor(ms / 3600000);
    if (hrs < 1) return "just now";
    return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  }
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) === 1 ? "" : "s"} ago`;
  if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? "" : "s"} ago`;
  return `${Math.floor(days / 365)} year${Math.floor(days / 365) === 1 ? "" : "s"} ago`;
}

export const Route = createFileRoute("/app/drills")({
  head: () => ({
    meta: buildAppMeta({
      title: "Drill library — CoachQuick",
      description: "Your reusable drill and homework library, organized by sport and skill for quick session planning.",
      path: "/app/drills",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/drills` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <LibraryPage />
    </RequireAuth>
  ),
});

function LibraryPage() {
  const { user } = useAuth();
  const userId = user?.id;
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<ItemType | "all">("all");
  const [open, setOpen] = useState(false);
  const [autoFillOpen, setAutoFillOpen] = useState(false);


  const [editingId, setEditingId] = useState<string | null>(null);
  const [creatingChildOf, setCreatingChildOf] = useState<Item | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [video, setVideo] = useState("");
  const [tags, setTags] = useState("");
  const [type, setType] = useState<ItemType>("technique");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imagePath, setImagePath] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<{ item: Item; childCount: number } | null>(
    null,
  );

  function resetForm() {
    setEditingId(null);
    setCreatingChildOf(null);
    setTitle("");
    setBody("");
    setVideo("");
    setTags("");
    setType("technique");
    setImageUrl(null);
    setImagePath(null);
  }

  function openNewParent() {
    resetForm();
    setOpen(true);
  }

  function openNewChild(parent: Item) {
    resetForm();
    setCreatingChildOf(parent);
    setType(parent.item_type);
    setOpen(true);
  }

  function openEdit(d: Item) {
    setEditingId(d.id);
    setCreatingChildOf(null);
    setTitle(d.title);
    setBody(d.body_text ?? "");
    setVideo(d.video_url ?? "");
    setTags((d.tags ?? []).join(", "));
    setType(d.item_type);
    setImageUrl(d.image_url ?? null);
    setImagePath(d.image_path ?? null);
    setOpen(true);
  }

  const itemsQuery = useQuery({
    queryKey: ["library", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from("drills")
        .select(
          "id, title, description, body_text, video_url, tags, item_type, image_url, image_path, parent_id, position, updated_at",
        )
        .eq("coach_id", userId!)
        .order("item_type")
        .order("position")
        .order("title");
      return (data as Item[]) ?? [];
    },
  });

  const items = itemsQuery.data ?? [];

  // Used-in-lessons counts (additive query — does not change existing queries)
  const usageQuery = useQuery({
    queryKey: ["library-usage", userId],
    enabled: !!userId && items.length > 0,
    queryFn: async () => {
      const ids = items.map((i) => i.id);
      if (ids.length === 0) return new Map<string, number>();
      const { data } = await supabase
        .from("session_drills")
        .select("drill_id")
        .in("drill_id", ids);
      const m = new Map<string, number>();
      for (const row of (data ?? []) as Array<{ drill_id: string }>) {
        m.set(row.drill_id, (m.get(row.drill_id) ?? 0) + 1);
      }
      return m;
    },
  });

  const usageMap = usageQuery.data ?? new Map<string, number>();
  const isChild = !!editingId && items.find((x) => x.id === editingId)?.parent_id;

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    setBusy(true);
    try {
      const parentItem = creatingChildOf;
      const tagList = tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      if (editingId) {
        const payload = {
          title,
          body_text: body || null,
          description: body || null,
          video_url: video || null,
          tags: tagList,
          ...(isChild ? {} : { item_type: type }),
          image_url: imageUrl,
          image_path: imagePath,
        };
        const { error } = await supabase
          .from("drills")
          .update(payload as never)
          .eq("id", editingId);
        if (error) throw error;
        toast.success("Updated");
      } else if (parentItem) {
        const siblings = items.filter((x) => x.parent_id === parentItem.id);
        const payload = {
          coach_id: user.id,
          parent_id: parentItem.id,
          title,
          item_type: parentItem.item_type,
          body_text: body || null,
          description: body || null,
          video_url: video || null,
          tags: tagList,
          image_url: imageUrl,
          image_path: imagePath,
          position: siblings.length,
        };
        const { error } = await supabase.from("drills").insert(payload as never);
        if (error) throw error;
        toast.success(`Added to ${parentItem.title}`);
      } else {
        const peers = items.filter((x) => !x.parent_id && x.item_type === type);
        const payload = {
          coach_id: user.id,
          parent_id: null,
          title,
          item_type: type,
          body_text: body || null,
          description: body || null,
          video_url: video || null,
          tags: tagList,
          image_url: imageUrl,
          image_path: imagePath,
          position: peers.length,
        };
        const { error } = await supabase.from("drills").insert(payload as never);
        if (error) throw error;
        toast.success("Added to library");
      }
      setOpen(false);
      resetForm();
      qc.invalidateQueries({ queryKey: ["library", userId] });
      qc.invalidateQueries({ queryKey: ["coach-stats", userId] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  function requestDelete(item: Item) {
    const childCount = items.filter((x) => x.parent_id === item.id).length;
    if (childCount === 0) {
      void doDelete(item, "cascade");
    } else {
      setDeleteTarget({ item, childCount });
    }
  }

  async function doDelete(item: Item, mode: "cascade" | "promote") {
    try {
      if (mode === "promote") {
        const { error: upErr } = await supabase
          .from("drills")
          .update({ parent_id: null } as never)
          .eq("parent_id", item.id);
        if (upErr) throw upErr;
      }
      const { error } = await supabase.from("drills").delete().eq("id", item.id);
      if (error) throw error;
      if (item.image_path) {
        await supabase.storage.from("library-media").remove([item.image_path]).catch(() => {});
      }
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["library", userId] });
      qc.invalidateQueries({ queryKey: ["coach-stats", userId] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    } finally {
      setDeleteTarget(null);
    }
  }

  function handleSeeded() {
    qc.invalidateQueries({ queryKey: ["library", userId] });
    qc.invalidateQueries({ queryKey: ["coach-stats", userId] });
  }


  // Group: top-level parents + their children, filtered by tab and search.
  const parents = items.filter((x) => !x.parent_id);
  const childrenByParent = new Map<string, Item[]>();
  for (const it of items) {
    if (it.parent_id) {
      const arr = childrenByParent.get(it.parent_id) ?? [];
      arr.push(it);
      childrenByParent.set(it.parent_id, arr);
    }
  }

  const search = q.toLowerCase().trim();
  function matches(it: Item): boolean {
    if (!search) return true;
    return (
      it.title.toLowerCase().includes(search) ||
      (it.body_text ?? "").toLowerCase().includes(search) ||
      (it.tags ?? []).some((t) => t.toLowerCase().includes(search))
    );
  }

  const visibleParents = parents.filter((p) => {
    if (tab !== "all" && p.item_type !== tab) return false;
    if (!search) return true;
    if (matches(p)) return true;
    const kids = childrenByParent.get(p.id) ?? [];
    return kids.some(matches);
  });

  const typeCounts = useMemo(() => {
    const c: Record<ItemType, number> = {
      technique: 0,
      drill: 0,
      recommendation: 0,
      resource: 0,
    };
    for (const it of parents) c[it.item_type]++;
    return c;
  }, [parents]);

  const lastEditedAt = useMemo(() => {
    let latest: string | null = null;
    for (const it of items) {
      if (it.updated_at && (!latest || it.updated_at > latest)) latest = it.updated_at;
    }
    return latest;
  }, [items]);

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {parents.length} item{parents.length === 1 ? "" : "s"} · last edited{" "}
              {timeAgo(lastEditedAt)}
            </p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight text-foreground md:text-5xl">
              Library
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Techniques, drills, recommendations, and resources you use in lessons. Add
              sub-techniques to break a skill into the specific things you teach.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setAutoFillOpen(true)}
              className="rounded-[10px]"
            >
              <Sparkles className="h-4 w-4" /> Auto-fill library
            </Button>

            <Dialog
              open={open}
              onOpenChange={(o) => {
                setOpen(o);
                if (!o) resetForm();
              }}
            >
              <DialogTrigger asChild>
                <Button onClick={openNewParent} className="rounded-[10px]">
                  <Plus className="h-4 w-4" /> Add item
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:rounded-[16px]">
                <DialogHeader>
                  <DialogTitle>
                    {editingId
                      ? isChild
                        ? "Edit sub-technique"
                        : "Edit library item"
                      : creatingChildOf
                        ? `Add sub-technique to ${creatingChildOf.title}`
                        : "New library item"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={add} className="space-y-4">
                  {!creatingChildOf && !isChild && (
                    <div>
                      <Label htmlFor="dtype">Type</Label>
                      <Select value={type} onValueChange={(v) => setType(v as ItemType)}>
                        <SelectTrigger id="dtype" className="mt-1.5">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technique">Technique</SelectItem>
                          <SelectItem value="drill">Drill</SelectItem>
                          <SelectItem value="recommendation">Recommendation</SelectItem>
                          <SelectItem value="resource">Resource</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label htmlFor="dt">Title</Label>
                    <Input
                      id="dt"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dd">Description (used in email)</Label>
                    <Textarea
                      id="dd"
                      rows={3}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      className="mt-1.5"
                      placeholder="The body text that appears in the recap email."
                    />
                  </div>
                  <div>
                    <Label htmlFor="dv">Link / Video URL (optional)</Label>
                    <Input
                      id="dv"
                      value={video}
                      onChange={(e) => setVideo(e.target.value)}
                      placeholder="https://…"
                      className="mt-1.5"
                    />
                  </div>
                  {!creatingChildOf && !isChild && (
                    <div>
                      <Label htmlFor="dtags">Tags (comma separated)</Label>
                      <Input
                        id="dtags"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        placeholder="footwork, dinks"
                        className="mt-1.5"
                      />
                    </div>
                  )}
                  {userId && (
                    <MediaPicker
                      coachId={userId}
                      value={imageUrl}
                      pathValue={imagePath}
                      videoUrl={video}
                      onChange={({ image_url, image_path }) => {
                        setImageUrl(image_url);
                        setImagePath(image_path);
                      }}
                    />
                  )}
                  <Button type="submit" className="w-full" disabled={busy}>
                    {busy
                      ? "Saving…"
                      : editingId
                        ? "Save changes"
                        : creatingChildOf
                          ? "Add sub-technique"
                          : "Add to library"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4 max-w-md">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search library…"
              className="h-10 rounded-[10px] border-transparent bg-transparent pl-9 text-sm focus-visible:border-border focus-visible:bg-card"
            />
          </div>
        </div>

        {/* Tab strip */}
        <div className="mb-5 border-b border-border">
          <div className="flex flex-wrap gap-6 text-sm">
            <TabBtn
              label={TYPE_LABEL.all}
              count={parents.length}
              active={tab === "all"}
              onClick={() => setTab("all")}
            />
            <TabBtn
              label={TYPE_LABEL.technique}
              count={typeCounts.technique}
              active={tab === "technique"}
              onClick={() => setTab("technique")}
            />
            <TabBtn
              label={TYPE_LABEL.drill}
              count={typeCounts.drill}
              active={tab === "drill"}
              onClick={() => setTab("drill")}
            />
            <TabBtn
              label={TYPE_LABEL.recommendation}
              count={typeCounts.recommendation}
              active={tab === "recommendation"}
              onClick={() => setTab("recommendation")}
            />
            <TabBtn
              label={TYPE_LABEL.resource}
              count={typeCounts.resource}
              active={tab === "resource"}
              onClick={() => setTab("resource")}
            />
          </div>
        </div>

        {itemsQuery.isLoading ? (
          <div className="grid gap-4 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-[16px] border bg-card p-6">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="mt-3 h-5 w-48" />
                <Skeleton className="mt-2 h-3 w-full" />
                <Skeleton className="mt-1 h-3 w-2/3" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-[16px] border bg-card p-10 text-center">
            <p className="text-sm font-semibold">Your library is empty.</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Auto-fill a starter set of techniques, drills, and recommendations for
              your sport — or add your first item by hand.
            </p>
            <Button
              className="mt-4 rounded-[10px]"
              onClick={() => setAutoFillOpen(true)}
            >
              <Sparkles className="h-4 w-4" /> Auto-fill library
            </Button>
          </div>

        ) : visibleParents.length === 0 ? (
          <div className="rounded-[16px] border bg-card p-10 text-center">
            <p className="text-sm text-muted-foreground">No matches.</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {visibleParents.map((p) => {
              const kids = (childrenByParent.get(p.id) ?? []).filter(
                (c) => !search || matches(p) || matches(c),
              );
              return (
                <LibraryCard
                  key={p.id}
                  item={p}
                  kids={kids}
                  usageMap={usageMap}
                  onEdit={openEdit}
                  onDelete={requestDelete}
                  onAddChild={openNewChild}
                />
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent className="sm:rounded-[16px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.item.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This item has {deleteTarget?.childCount} sub-
              {TYPE_SINGULAR[deleteTarget?.item.item_type ?? "technique"]}
              {deleteTarget && deleteTarget.childCount === 1 ? "" : "s"}. You can delete them
              too, or keep them as their own top-level items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => deleteTarget && doDelete(deleteTarget.item, "promote")}
            >
              Keep sub-items
            </Button>
            <AlertDialogAction
              onClick={() => deleteTarget && doDelete(deleteTarget.item, "cascade")}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AutoFillDialog
        open={autoFillOpen}
        onOpenChange={setAutoFillOpen}
        userId={userId}
        existingItemCount={items.length}
        onSeeded={handleSeeded}
      />
    </AppShell>

  );
}

function TabBtn({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative -mb-px flex items-center gap-2 border-b-2 pb-3 text-sm font-semibold transition-colors ${
        active
          ? "border-foreground text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
      <span
        className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
          active ? "bg-foreground text-background" : "bg-muted text-muted-foreground"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function LibraryCard({
  item,
  kids,
  usageMap,
  onEdit,
  onDelete,
  onAddChild,
}: {
  item: Item;
  kids: Item[];
  usageMap: Map<string, number>;
  onEdit: (i: Item) => void;
  onDelete: (i: Item) => void;
  onAddChild: (i: Item) => void;
}) {
  const meta = TYPE_META[item.item_type];
  const Icon = meta.icon;
  const usage = usageMap.get(item.id) ?? 0;
  return (
    <div className="flex flex-col overflow-hidden rounded-[16px] border bg-card">
      {item.image_url && (
        <div className="aspect-video w-full overflow-hidden bg-muted">
          <img
            src={item.image_url}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${meta.cls}`}
          >
            {Icon && <Icon className="h-3 w-3 fill-current" />}
            {meta.label}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onEdit(item)}
              aria-label="Edit"
              className="flex h-7 w-7 items-center justify-center rounded-[8px] border text-muted-foreground hover:text-foreground"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Item actions"
                  className="flex h-7 w-7 items-center justify-center rounded-[8px] border text-muted-foreground hover:text-foreground"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => onEdit(item)}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onAddChild(item)}>
                  <Plus className="h-3.5 w-3.5" /> Add sub-{TYPE_SINGULAR[item.item_type]}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => onDelete(item)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <h3 className="mt-3 text-[17px] font-bold leading-tight">{item.title}</h3>
        {item.body_text && (
          <p className="mt-2 text-[13.5px] leading-[1.55] text-muted-foreground">
            {item.body_text}
          </p>
        )}
        {item.video_url && (
          <a
            href={item.video_url}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            <Video className="h-3 w-3" /> Open link
          </a>
        )}
        <div className="mt-4 flex items-end justify-between gap-3">
          <div className="flex flex-wrap gap-1.5">
            {(item.tags ?? []).map((t) => (
              <span
                key={t}
                className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
              >
                {t}
              </span>
            ))}
          </div>
          <p className="shrink-0 text-[11px] text-muted-foreground">
            Used in {usage} lesson{usage === 1 ? "" : "s"}
          </p>
        </div>

        {/* Sub-techniques */}
        <div className="mt-4 border-t pt-3">
          <div className="flex items-baseline justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Sub-{TYPE_SINGULAR[item.item_type]}s
              {kids.length > 0 && <span className="ml-1">· {kids.length}</span>}
            </p>
          </div>
          {kids.length === 0 ? (
            <button
              type="button"
              onClick={() => onAddChild(item)}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-[10px] border border-dashed border-border px-3 py-2 text-xs font-semibold text-muted-foreground hover:border-foreground/30 hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" /> Add sub-{TYPE_SINGULAR[item.item_type]}
            </button>
          ) : (
            <div className="mt-2 space-y-1.5">
              {kids.map((c) => (
                <SubItem
                  key={c.id}
                  item={c}
                  parentType={item.item_type}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
              <button
                type="button"
                onClick={() => onAddChild(item)}
                className="flex w-full items-center justify-center gap-1.5 rounded-[10px] border border-dashed border-border px-3 py-2 text-xs font-semibold text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" /> Add another
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SubItem({
  item,
  parentType,
  onEdit,
  onDelete,
}: {
  item: Item;
  parentType: ItemType;
  onEdit: (i: Item) => void;
  onDelete: (i: Item) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-[10px] bg-[#e8ebe6] dark:bg-white/5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="group flex w-full items-center gap-2 px-3 py-2 text-left"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="flex-1 truncate text-sm font-semibold">{item.title}</span>
        <span className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(item);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onEdit(item);
              }
            }}
            aria-label="Edit"
            className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-background hover:text-foreground"
          >
            <Pencil className="h-3 w-3" />
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onDelete(item);
              }
            }}
            aria-label="Delete"
            className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-background hover:text-destructive"
          >
            <Trash2 className="h-3 w-3" />
          </span>
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 pl-9">
          {item.body_text ? (
            <p className="text-xs leading-[1.55] text-muted-foreground">{item.body_text}</p>
          ) : (
            <p className="text-xs italic text-muted-foreground">
              No description for this sub-{TYPE_SINGULAR[parentType]} yet.
            </p>
          )}
          {item.video_url && (
            <a
              href={item.video_url}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
            >
              <Video className="h-3 w-3" /> Open link
            </a>
          )}
        </div>
      )}
    </div>
  );
}
